// Wildfire perimeter layer family.
//
// NIFC WFIGS Interagency Perimeters Current — one GeoJSON source drives
// two layers: a semi-transparent fill and a border line, both in fire
// orange (#ff5a1f). Layers are mounted VISIBLE by default (no
// layout.visibility block in the original addOverlays.ts inline code —
// preserved byte-identical here as required by the HI-1 batch-7 spec).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 1 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, and
// layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently (same
// pattern retrofitted onto batch-6b rasters after boss caught the
// dropout).

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addFiresLayers(map: MlMap): void {
  if (!map.getSource('fires')) {
    map.addSource('fires', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'fires-fill',
      type: 'fill',
      source: 'fires',
      paint: {
        'fill-color': '#ff5a1f',
        'fill-opacity': 0.25,
      },
    });
    map.addLayer({
      id: 'fires-line',
      type: 'line',
      source: 'fires',
      paint: {
        'line-color': '#ff5a1f',
        'line-width': 1.5,
      },
    });
  }
}

registerLayerFamily({
  id: 'fires',
  label: 'Wildfire Perimeters',
  sourceId: 'fires',
  layerIds: ['fires-fill', 'fires-line'],
  mount: (map) => addFiresLayers(map),
  visibilitySelector: (l) => l.fires,
  applyState: (map) => {
    if (!map.getLayer('fires-fill')) return;
    const show = useLayerVisibilityStore.getState().layers.fires;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('fires-fill', 'visibility', v);
    map.setLayoutProperty('fires-line', 'visibility', v);
  },
  bboxRefresh: (map) => {
    const spec = OVERLAY_BY_ID.get('fires');
    if (!spec) return;
    // Fires refresh on every viewport change (always loaded).
    return refreshOverlay(map, spec);
  },
});
